import type { Practitioner } from '@/mocks/practitioners';

interface PractitionerInfoProps {
  practitioner: Practitioner;
}

export default function PractitionerInfo({ practitioner: p }: PractitionerInfoProps) {
  const conventionLabel = p.conventionCode
    ? `Partenaire (code ${p.conventionCode})`
    : 'Non partenaire';

  const infoItems: { icon: string; label: string; value: string; href?: string }[] = [
    { icon: 'ri-stethoscope-line', label: 'Spécialité', value: p.type },
    { icon: 'ri-price-tag-3-line', label: 'Catégorie', value: p.category },
    { icon: 'ri-map-pin-2-line', label: 'Quartier', value: `${p.district}, Rabat` },
    { icon: 'ri-home-4-line', label: 'Adresse', value: p.address },
    {
      icon: 'ri-phone-line',
      label: 'Téléphone',
      value: p.phone ?? 'Non communiqué',
      href: p.phone ? `tel:${p.phone.replace(/\s+/g, '')}` : undefined,
    },
    {
      icon: 'ri-wallet-3-line',
      label: 'Tarif',
      value: p.price > 0
        ? `Consultation dès ${p.price} MAD`
        : (p.category === 'Pharmacies' ? 'Tarifs en pharmacie' : 'Tarif sur demande'),
    },
    { icon: 'ri-shield-check-line', label: 'Code partenaire', value: conventionLabel },
    { icon: 'ri-calendar-line', label: 'Prochain créneau', value: p.nextSlot },
  ];

  const mapQuery = encodeURIComponent(`${p.address}, ${p.district}, Rabat, Maroc`);

  return (
    <div className="space-y-6">
      <section className="bg-background-50 border border-background-200 rounded-2xl p-6 md:p-8">
        <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-4">
          À propos
        </h2>
        <p className="text-foreground-600 leading-relaxed mb-3">
          <strong className="text-foreground-900">{p.name}</strong> — {p.type} ({p.category})
          {' '}vous accueille à {p.district}, Rabat.
          {p.conventionCode
            ? ' Praticien partenaire, vous bénéficiez d\'un parcours de soins orienté vers votre réseau partenaire.'
            : ''}
        </p>
        <p className="text-foreground-600 leading-relaxed">
          Consultez ses informations pratiques et ses disponibilités ci-dessous, puis réservez
          votre rendez-vous en ligne en quelques secondes. La confirmation est envoyée directement
          au praticien par WhatsApp.
        </p>
      </section>

      <section className="bg-background-50 border border-background-200 rounded-2xl p-6 md:p-8">
        <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-5">
          Informations pratiques
        </h2>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
          {infoItems.map((item) => (
            <div key={item.label} className="flex items-start gap-3">
              <span className="shrink-0 w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-primary-600">
                <i className={`${item.icon} text-lg w-5 h-5 flex items-center justify-center`} />
              </span>
              <div className="min-w-0">
                <dt className="text-xs font-semibold uppercase tracking-wide text-foreground-500">
                  {item.label}
                </dt>
                <dd className="text-sm text-foreground-900 mt-0.5">
                  {item.href ? (
                    <a
                      href={item.href}
                      className="text-primary-700 hover:text-primary-800 font-medium cursor-pointer"
                    >
                      {item.value}
                    </a>
                  ) : (
                    item.value
                  )}
                </dd>
              </div>
            </div>
          ))}
        </dl>
      </section>

      <section className="bg-background-50 border border-background-200 rounded-2xl p-6 md:p-8">
        <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-4">
          Localisation
        </h2>
        <div className="w-full h-[320px] md:h-[380px] rounded-xl overflow-hidden border border-background-200">
          <iframe
            title={`Localisation de ${p.name}`}
            src={`https://www.google.com/maps?q=${mapQuery}&output=embed`}
            className="w-full h-full"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
        <p className="text-sm text-foreground-600 flex items-center gap-1.5 mt-4">
          <i className="ri-map-pin-line text-foreground-400 w-4 h-4 flex items-center justify-center" />
          {p.address}
          {p.district ? ` · ${p.district}, Rabat` : ''}
        </p>
      </section>
    </div>
  );
}